import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { TourFigure, TOUR_PHOTOS_2026 } from '../components/TourFigure';
import { Footer } from '../components/Footer';
import { translations } from '../translations';
import './seo-pages.css';
import './PrivateIstanbulTours.css';
import './AuthorProfile.css';

const AKIN_PHOTO = '/images/akin-guzel-portrait.webp';
const AKIN_PHOTO_SCHEMA = 'https://customer-assets.emergentagent.com/job_69f6d9a2-9928-4a45-bdbe-bb2ce971af03/artifacts/kse600f1_ak%C4%B1nnnnn.webp';

export default function AuthorProfilePage({ language }) {
  const t = translations[language];

  const personSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "@id": "https://viaturkey.net/about/akin-guzel/#person",
        "name": "Akın Güzel",
        "alternateName": "Akin Guzel",
        "givenName": "Akın",
        "familyName": "Güzel",
        "jobTitle": "Licensed Tour Guide & Travel Consultant",
        "worksFor": {
          "@type": "Organization",
          "name": "Viaturkey",
          "url": "https://viaturkey.net"
        },
        "url": "https://viaturkey.net/about/akin-guzel",
        "image": AKIN_PHOTO_SCHEMA,
        "description": "Licensed tour guide and travel consultant based in Turkey with 17+ years of experience in tourism, citizenship advisory, and real estate consulting. Founder of Viaturkey. Erciyes University graduate, Faculty of Tourism — Tour Guiding.",
        "alumniOf": {
          "@type": "CollegeOrUniversity",
          "name": "Erciyes University",
          "department": "Faculty of Tourism — Tour Guiding Department"
        },
        "hasCredential": [
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "license",
            "name": "Professional Tour Guide License (Kokart)",
            "recognizedBy": {
              "@type": "GovernmentOrganization",
              "name": "Republic of Turkey, Ministry of Culture and Tourism"
            }
          },
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "degree",
            "name": "Bachelor of Arts in Tourism — Tour Guiding"
          }
        ],
        "knowsLanguage": ["Turkish", "English", "Russian", "Arabic"],
        "knowsAbout": [
          "Turkish Citizenship by Investment",
          "Istanbul Private Tours",
          "Real Estate Consulting Turkey",
          "Turkish Residence Permits",
          "Travel Itinerary Planning"
        ],
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "TR",
          "addressLocality": "Istanbul"
        },
        "sameAs": [
          "https://www.instagram.com/via_turkey",
          "https://www.youtube.com/@akinguzel1490",
          "https://www.tiktok.com/@akingzel",
          "https://x.com/akngzl3",
          "https://www.tripadvisor.com/Attraction_Review-g293974-d25289505-Reviews-or10-Viaturkey_Tours-Istanbul.html"
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://viaturkey.net/" },
          { "@type": "ListItem", "position": 2, "name": "About", "item": "https://viaturkey.net/about" },
          { "@type": "ListItem", "position": 3, "name": "Akın Güzel", "item": "https://viaturkey.net/about/akin-guzel" }
        ]
      }
    ]
  };

  return (
    <div className="pit-page" data-testid="author-profile-page">
      <SEOHead
        title="Akın Güzel — Licensed Tour Guide & Travel Consultant | Viaturkey"
        description="Meet Akın Güzel, licensed tour guide and founder of Viaturkey. 17+ years of experience in Istanbul tourism, Turkish citizenship advisory, and real estate consulting. Erciyes University graduate."
        canonical="https://viaturkey.net/about/akin-guzel"
        schema={personSchema}
      />

      {/* Hero */}
      <section className="pit-hero author-hero" data-testid="author-hero">
        <div className="pit-hero-overlay"></div>
        <div className="pit-hero-content">
          <h1>Akın Güzel</h1>
          <p>Licensed Tour Guide & Travel Consultant — Founder of Viaturkey</p>
        </div>
      </section>

      {/* Content */}
      <article className="pit-container">
        <div className="pit-body" data-testid="author-body">

          {/* Author Card */}
          <div className="author-card" data-testid="author-card">
            <div className="author-card-photo">
              <img src={AKIN_PHOTO} alt="Akın Güzel — Licensed Tour Guide & Founder of Viaturkey" width="830" height="1456" data-testid="author-photo" />
            </div>
            <div className="author-card-info">
              <h2>Akın Güzel</h2>
              <p className="author-card-title">Founder & Lead Travel Consultant</p>
              <p className="author-card-subtitle">Licensed Tour Guide | Turkish Citizenship Advisor | Real Estate Consultant</p>
              <div className="author-card-meta">
                <span><i className="fas fa-map-marker-alt"></i> Istanbul, Türkiye</span>
                <span><i className="fas fa-calendar-alt"></i> 17+ years experience</span>
                <span><i className="fas fa-users"></i> 1,000+ clients served</span>
                <span><i className="fas fa-graduation-cap"></i> Erciyes University</span>
              </div>
            </div>
          </div>

          {/* Bio */}
          <h2>About Akın</h2>
          <p>
            Akın Güzel is a licensed tour guide and travel consultant based in Turkey, with over 17 years of professional experience in tourism, citizenship advisory, and real estate consulting for international clients.
          </p>
          <p>
            A graduate of Erciyes University, Faculty of Tourism — Tour Guiding Department, Akın holds an official Professional Tour Guide License (Kokart) issued by the Turkish Ministry of Culture and Tourism. He founded Viaturkey in 2016 with a single mission: helping international travelers and investors navigate Turkey with clarity, transparency, and personalized guidance.
          </p>
          <p>
            Over the past decade, Akın has personally served more than 1,000 clients, primarily from the United States, the United Arab Emirates, and Russia. His expertise spans private cultural tours of Istanbul, Turkish citizenship by investment processes, residence permit guidance, and luxury real estate consulting for foreign buyers.
          </p>

          <TourFigure photo={TOUR_PHOTOS_2026.sultanahmetRooftop} testId="author-photo-sultanahmet-rooftop" />

          {/* Expertise */}
          <h2>Areas of Expertise</h2>
          <ul className="pit-trust-list" data-testid="author-expertise">
            <li><strong>Licensed Tour Guiding</strong> — Istanbul and Turkish historic sites. Professional Tour Guide License (Kokart) issued by the Ministry of Culture and Tourism.</li>
            <li><strong>Turkish Citizenship by Investment Advisory</strong> — Expert guidance on Turkey's citizenship-by-investment program, including property acquisition routes and legal requirements.</li>
            <li><strong>Real Estate Consulting for Foreign Buyers</strong> — Guides international investors through Istanbul's property market, including due diligence, legal processes, and ROI analysis.</li>
            <li><strong>Residence Permit & Long-Term Stay Guidance</strong> — Assists with Turkish residence permit applications, documentation, and ongoing compliance.</li>
            <li><strong>Travel Itinerary Planning for International Visitors</strong> — Custom itineraries designed for Gulf, European, and American travelers with multilingual support.</li>
          </ul>

          {/* Credentials */}
          <h2>Credentials & Education</h2>
          <div className="author-credentials" data-testid="author-credentials">
            <div className="author-credential-item">
              <i className="fas fa-graduation-cap"></i>
              <div>
                <strong>Erciyes University</strong>
                <p>Faculty of Tourism — Tour Guiding (B.A.)</p>
              </div>
            </div>
            <div className="author-credential-item">
              <i className="fas fa-id-badge"></i>
              <div>
                <strong>Professional Tour Guide License (Kokart)</strong>
                <p>Republic of Turkey, Ministry of Culture and Tourism</p>
              </div>
            </div>
            <div className="author-credential-item">
              <i className="fas fa-briefcase"></i>
              <div>
                <strong>17+ Years of Professional Tourism Experience</strong>
                <p>Active since 2008 across tourism, investment advisory, and real estate consulting</p>
              </div>
            </div>
            <div className="author-credential-item">
              <i className="fas fa-globe-americas"></i>
              <div>
                <strong>1,000+ International Clients Served</strong>
                <p>Primarily from the United States, UAE, and Russia (2008–2026)</p>
              </div>
            </div>
          </div>

          {/* Languages */}
          <h2>Languages</h2>
          <div className="author-languages" data-testid="author-languages">
            <span className="author-lang-badge">Turkish (Native)</span>
            <span className="author-lang-badge">English (Fluent)</span>
            <span className="author-lang-badge">Russian (Conversational)</span>
            <span className="author-lang-badge">Arabic (Basic)</span>
          </div>

          {/* Connect */}
          <h2>Connect with Akın</h2>
          <div className="author-social-links" data-testid="author-social">
            <a href="https://wa.me/905075337545?text=Hi%20Ak%C4%B1n%2C%20I%20found%20your%20profile%20on%20Viaturkey." target="_blank" rel="noopener noreferrer" className="author-social-link whatsapp">
              <i className="fab fa-whatsapp"></i> WhatsApp
            </a>
            <a href="mailto:akin@viaturkey.net" className="author-social-link email">
              <i className="fas fa-envelope"></i> Email
            </a>
            <a href="https://www.instagram.com/via_turkey" target="_blank" rel="noopener noreferrer" className="author-social-link instagram">
              <i className="fab fa-instagram"></i> Instagram
            </a>
            <a href="https://www.youtube.com/@akinguzel1490" target="_blank" rel="noopener noreferrer" className="author-social-link youtube">
              <i className="fab fa-youtube"></i> YouTube
            </a>
            <a href="https://www.tiktok.com/@akingzel" target="_blank" rel="noopener noreferrer" className="author-social-link tiktok">
              <i className="fab fa-tiktok"></i> TikTok
            </a>
            <a href="https://x.com/akngzl3" target="_blank" rel="noopener noreferrer" className="author-social-link twitter">
              <i className="fab fa-x-twitter"></i> X (Twitter)
            </a>
            <a href="https://www.tripadvisor.com/Attraction_Review-g293974-d25289505-Reviews-or10-Viaturkey_Tours-Istanbul.html" target="_blank" rel="noopener noreferrer" className="author-social-link tripadvisor">
              <i className="fab fa-tripadvisor"></i> TripAdvisor
            </a>
          </div>

          {/* TripAdvisor */}
          <div className="pit-tripadvisor" data-testid="author-tripadvisor">
            <h3><i className="fab fa-tripadvisor"></i> Verified Reviews on TripAdvisor</h3>
            <p>Read verified guest reviews from travelers who have worked with Akın and Viaturkey.</p>
            <a href="https://www.tripadvisor.com/Attraction_Review-g293974-d25289505-Reviews-or10-Viaturkey_Tours-Istanbul.html" target="_blank" rel="noopener noreferrer" className="pit-tripadvisor-link" data-testid="author-tripadvisor-link">
              <i className="fab fa-tripadvisor"></i> Read Reviews on TripAdvisor
            </a>
          </div>

          {/* Related Pages */}
          <h2>Explore Viaturkey</h2>
          <div className="sp-related-links" data-testid="author-related-links">
            <Link to="/private-istanbul-tours" className="sp-related-link"><i className="fas fa-map-signs"></i> Private Istanbul Tours</Link>
            <Link to="/istanbul-old-city-tour" className="sp-related-link"><i className="fas fa-mosque"></i> Istanbul Old City Tour</Link>
            <Link to="/real-estate" className="sp-related-link"><i className="fas fa-building"></i> Real Estate Investment</Link>
            <Link to="/contact" className="sp-related-link"><i className="fas fa-headset"></i> Contact Viaturkey</Link>
          </div>
        </div>

        {/* Final CTA */}
        <div className="sp-cta" data-testid="author-cta">
          <h2>Ready to Plan Your Istanbul Experience?</h2>
          <p>Reach out to Akın directly. Personalized consultation within 2 hours — no obligations.</p>
          <a href="https://wa.me/905075337545?text=Hi%20Ak%C4%B1n%2C%20I%27d%20like%20to%20plan%20my%20Istanbul%20trip." target="_blank" rel="noopener noreferrer" className="sp-cta-btn" data-testid="author-whatsapp-cta">
            <i className="fab fa-whatsapp"></i> Get Travel Consultation
          </a>
        </div>
      </article>

      <Footer t={t} />
    </div>
  );
}
